import { MAX_FILE_BYTES, SearchQuery, SearchResult, defineEndpoint } from '@itc/shared';
import { z } from 'zod';
import { Entry, EntryInput, EntryUpdate, OrgInfo, RecordId, Report, ReportInput, ReportSummary, ReportUpdate } from './schemas.ts';

// The Library's contracts (design §7.3, plan 2E). Every change takes an
// Idempotency-Key. `GET /library/org` and `GET /library/search` are the only routes open to any login.

const ById = z.object({ id: RecordId });
const Removed = z.object({ ok: z.literal(true) });

/** Every entry, with its body, newest change first. Fine at Sierra's scale: a knowledge base is hundreds of entries, not thousands. */
export const listEntries = defineEndpoint({ method: 'GET', path: '/library/entries', permission: 'library.view', response: z.object({ items: z.array(Entry) }) });

export const createEntry = defineEndpoint({ method: 'POST', path: '/library/entries', permission: 'library.manage', body: EntryInput, response: Entry, idempotencyKey: 'required' });

export const updateEntry = defineEndpoint({ method: 'PUT', path: '/library/entries/:id', permission: 'library.manage', params: ById, body: EntryUpdate, response: Entry, idempotencyKey: 'required' });

export const deleteEntry = defineEndpoint({ method: 'DELETE', path: '/library/entries/:id', permission: 'library.manage', params: ById, response: Removed, idempotencyKey: 'required' });

/** Saved reports, newest change first, without their bodies. */
export const listReports = defineEndpoint({ method: 'GET', path: '/library/reports', permission: 'library.view_reports', response: z.object({ items: z.array(ReportSummary) }) });

export const getReport = defineEndpoint({ method: 'GET', path: '/library/reports/:id', permission: 'library.view_reports', params: ById, response: Report });

export const createReport = defineEndpoint({ method: 'POST', path: '/library/reports', permission: 'library.manage_reports', body: ReportInput, response: Report, idempotencyKey: 'required' });

export const updateReport = defineEndpoint({ method: 'PUT', path: '/library/reports/:id', permission: 'library.manage_reports', params: ById, body: ReportUpdate, response: Report, idempotencyKey: 'required' });

export const deleteReport = defineEndpoint({ method: 'DELETE', path: '/library/reports/:id', permission: 'library.manage_reports', params: ById, response: Removed, idempotencyKey: 'required' });

/** R15: what the caller may find. Open to any login, as each kind has its own permission, checked inside. */
export const searchLibrary = defineEndpoint({ method: 'GET', path: '/library/search', permission: 'authenticated', query: SearchQuery, response: SearchResult });

/** Posts a drawn card to the cards channel (plan 3D, F4). The body is the image. A key per draw, so a double-tap posts once. */
export const postCard = defineEndpoint({
  method: 'POST',
  path: '/library/cards',
  permission: 'library.post_cards',
  upload: { maxBytes: MAX_FILE_BYTES },
  rateLimit: { max: 10 },
  idempotencyKey: 'required',
  response: z.object({ id: RecordId, commandId: RecordId }),
});

/** The card footer, countdown and welcome card's text (Assumption 5). */
export const getOrg = defineEndpoint({ method: 'GET', path: '/library/org', permission: 'authenticated', response: OrgInfo });
